// JWT decoding and HS256 verification, fully client-side (Web Crypto for HMAC).

export interface DecodedJwt {
  header: unknown;
  payload: unknown;
  signature: string;
}

function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function b64urlToText(s: string): string {
  return new TextDecoder().decode(b64urlToBytes(s));
}

function bytesToB64url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decodes a JWT into its header and payload objects (signature kept as text). */
export function decodeJwt(token: string): DecodedJwt {
  const parts = token.trim().split('.');
  if (parts.length !== 3) {
    throw new Error('A JWT has three dot-separated parts');
  }
  return {
    header: JSON.parse(b64urlToText(parts[0])),
    payload: JSON.parse(b64urlToText(parts[1])),
    signature: parts[2],
  };
}

/** Verifies an HS256 JWT signature against the given secret. */
export async function verifyHs256(token: string, secret: string): Promise<boolean> {
  const parts = token.trim().split('.');
  if (parts.length !== 3) return false;
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    Uint8Array.from(enc.encode(secret)),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, Uint8Array.from(enc.encode(`${parts[0]}.${parts[1]}`)));
  return bytesToB64url(new Uint8Array(sig)) === parts[2];
}
